import { useMemo, useRef } from 'react';
import type { CollabEditorCommentsOptions } from '@nimbalyst/collab-bundle/editor';
import { getCollabDocsSession, useSharedDocumentTitles } from '@nimbalyst/collab-bundle/docs-ui';
import { buildCollabUri } from '@nimbalyst/collab-protocol';
import { getCommentMembers } from './orgRoster';
import { WebCollabDocumentsDataSource } from './WebCollabDocumentsDataSource';
import { webCollabScopeKey } from './WebCollabHost';

type CommentMentionPayload = Parameters<NonNullable<CollabEditorCommentsOptions['onMention']>>[1];

/**
 * The comment seam the browser editor mounts with.
 *
 * Everything here has to survive a mount that happens once and reads its
 * options object once: `mountCollabEditor` shallow-copies `comments` when it
 * builds the editor config, so a value replaced on a later render is never
 * seen. Anything that resolves asynchronously therefore has to reach the
 * runtime through a function, or through a nested object whose identity the
 * copy preserves — never by handing this hook's caller a new object.
 */
export function useDocumentComments({
  orgId,
  memberId,
  presenceName,
  projectId,
  documentId,
  canComment,
}: {
  orgId: string;
  memberId: string;
  presenceName: string;
  projectId: string;
  documentId: string;
  /**
   * Whether this user's org role permits authoring comments. Derived from the
   * REST roster, so it starts false on a cold open and resolves later; the
   * editor mount is what tells the bundle to re-read it.
   */
  canComment: boolean;
}): CollabEditorCommentsOptions {
  const documentTitle = useSharedDocumentTitles().get(documentId) ?? '';
  /**
   * The author identity, updated in place.
   *
   * The runtime reads `currentUser.name` when a comment is submitted and writes
   * it into the Y.Doc as the thread's permanent author label. The display name
   * comes from the REST roster, which resolves after the editor has already
   * mounted, so a replacement object would arrive too late and every comment on
   * the first document opened in a session would be signed with a raw Stytch
   * member id. The shallow copy above keeps this nested reference, so updating
   * it is what lets the resolved name reach a comment authored later without
   * remounting a live editor.
   */
  const currentUserRef = useRef({ id: memberId, name: presenceName });
  currentUserRef.current.id = memberId;
  currentUserRef.current.name = presenceName;
  // Read at notification time rather than captured, for the same reason: on a
  // cold deep link the document index has not resolved when the editor mounts.
  const liveRef = useRef({ documentTitle, scopeKey: webCollabScopeKey(orgId, projectId) });
  liveRef.current = { documentTitle, scopeKey: webCollabScopeKey(orgId, projectId) };
  /**
   * The comment-authoring answer, updated in place for the same reason as the
   * author identity above: it comes from the REST roster, which resolves after
   * the editor has mounted and read this object once. Handing the caller a
   * replacement object would arrive too late, and the bundle resolves this
   * through the function below every time it needs the answer — at render and
   * again at mutation time — so a mid-session revocation is never stale.
   */
  const canCommentRef = useRef(canComment);
  canCommentRef.current = canComment;

  return useMemo(() => {
    const notify = (
      reason: 'mention' | 'reply',
      recipientUserIds: string[],
      payload: CommentMentionPayload,
    ): void => {
      // Recipients arrive already stripped of the author by the shared runtime;
      // filtering again here would only risk disagreeing with it.
      if (recipientUserIds.length === 0) return;
      // The TeamRoom socket belongs to the Shared Docs session, not to this
      // route's DocumentRoom connection. It is resolved per notification rather
      // than per render because the session outlives and predates this route.
      const dataSource = getCollabDocsSession(liveRef.current.scopeKey)?.dataSource;
      if (!(dataSource instanceof WebCollabDocumentsDataSource)) {
        console.error(
          '[documentComments] No TeamRoom connection to announce a document comment on',
          liveRef.current.scopeKey,
        );
        return;
      }
      dataSource.notifyDocumentComment({
        documentId,
        reason,
        recipientUserIds,
        payload: {
          actorName: payload.actorName,
          snippet: payload.snippet,
          commentId: payload.commentId,
          threadId: payload.threadId,
          sourceTitle: liveRef.current.documentTitle,
        },
      });
    };
    return {
      currentUser: currentUserRef.current,
      // Synchronous by contract, and snapshotted once per composer mount. It
      // reads the shared roster cache the route started loading at mount.
      getMembers: () => getCommentMembers(orgId, memberId),
      canComment: () => canCommentRef.current,
      // Only ever reaches a notification's `sourceTitle`, which `notify`
      // overrides with the live title above.
      documentTitle,
      documentId,
      documentUri: buildCollabUri(orgId, documentId),
      onMention: (recipientUserIds, payload) => notify('mention', recipientUserIds, payload),
      onReply: (recipientUserIds, payload) => notify('reply', recipientUserIds, payload),
    };
    // `documentTitle` is deliberately absent: it is read live through the ref,
    // and depending on it would rebuild an object the mount cannot re-read.
  }, [documentId, memberId, orgId]);
}
